<template>
  <AppShell
    role-label="Student"
    :nav-items="navItems"
    :active-view="activeView"
    :account="auth.user || {}"
    :collapsed="navCollapsed"
    @navigate="openNav"
    @logout="signOut"
    @toggle-nav="navCollapsed = !navCollapsed"
  >
    <div class="student-workspace" :class="{ 'study-ui': activeView === 'task' }">
      <PageHeader eyebrow="Student workspace" :title="pageTitle" :description="pageDescription" />
      <div v-if="overviewMessage && activeView === 'overview'" class="ui-notice" role="status">{{ overviewMessage }}</div>

      <section v-if="activeView === 'task'" class="task-stack">
        <div v-if="loading" class="ui-empty" role="status">Loading your task…</div>
        <div v-else-if="taskError || taskEntry.error" class="ui-notice" role="alert">
          <p>{{ taskError || taskEntry.error }}</p>
          <button type="button" class="ui-button ui-button--secondary" @click="loadTask">Retry</button>
        </div>
        <article v-else-if="nextAssignment" class="workspace-card task-card">
          <label v-if="taskOptions.length > 1" class="task-selector">
            Choose task
            <select class="ui-select" :value="taskEntry.key" @change="selectedTaskKey = $event.target.value">
              <option v-for="option in taskOptions" :key="option.key" :value="option.key">{{ option.title }}</option>
            </select>
          </label>
          <div class="task-card__topline">
            <div>
              <p class="assignment-label">Research proposal</p>
              <h2>{{ taskTitle }}</h2>
            </div>
            <span v-if="taskEntry.participant?.mode === 'preview'" class="study-preview">Preview · test environment</span>
            <span v-else-if="taskEntry.participant?.mode === 'uat'" class="study-preview">API test · not research data</span>
            <StatusPill v-else-if="!taskEntry.participant || taskEntry.participant.mode === 'classroom'" :value="nextAssignment.status" />
          </div>
          <StudySteps v-if="taskEntry.participant && taskEntry.participant.mode !== 'classroom'" :participant="taskEntry.participant" />
          <div class="task-next">
            <div>
              <p class="assignment-label">{{ taskEntry.step === 3 ? 'Finished' : 'Your next step' }}</p>
              <h3>{{ taskEntry.label }}</h3>
              <p>{{ taskEntry.guidance || (taskEntry.participant ? taskGuidance : 'Read the task instructions, then open your writing workspace.') }}</p>
            </div>
            <button type="button" class="ui-button ui-button--primary task-action" :disabled="!taskEntry.to" @click="$router.push(taskEntry.to)">{{ taskEntry.label }}</button>
          </div>
          <p v-if="taskEntry.participant?.pretest_available" class="study-note task-return-note">Use Pre-test above to revisit your answers. Changes are allowed until the post-test is complete.</p>
          <details class="task-brief" :open="!taskEntry.participant || taskEntry.participant.mode === 'classroom'">
          <summary>Task instructions <span>View brief and teacher notes</span></summary>
          <div class="task-instructions">{{ nextAssignment.prompt }}</div>
          <div v-if="nextAssignment.teacher_note" class="teacher-note">
            <strong>Teacher note</strong>
            <p>{{ nextAssignment.teacher_note }}</p>
          </div>
          <dl class="task-facts">
            <div v-if="nextAssignment.start_at"><dt>Start</dt><dd>{{ formatDateTime(nextAssignment.start_at) }}</dd></div>
            <div v-if="nextAssignment.due_at"><dt>Due</dt><dd class="deadline">{{ formatDateTime(nextAssignment.due_at) }}</dd></div>
            <div><dt>Teacher</dt><dd>{{ nextAssignment.created_by?.display_name || nextAssignment.created_by?.username || 'Teacher' }}</dd></div>
          </dl>
          </details>
        </article>
        <div v-else-if="!loading" class="ui-empty">No task is available yet.</div>
      </section>

      <section v-if="activeView === 'overview'" class="overview-stack">
        <MetricStrip :items="studentMetrics" />

        <div class="overview-grid">
          <article class="workspace-card next-action-card">
            <SectionHeader title="Next action" description="Pick up the writing task that needs your attention now." />
            <div v-if="nextAssignment" class="next-assignment">
              <div class="next-assignment__heading">
                <div>
                  <p class="assignment-label">Assignment</p>
                  <h2>{{ nextAssignment.title }}</h2>
                </div>
                <StatusPill :value="nextAssignment.status" />
              </div>
              <p class="next-assignment__prompt">{{ nextAssignment.prompt }}</p>
              <dl class="assignment-facts">
                <div><dt>Due</dt><dd class="deadline">{{ formatDateTime(nextAssignment.due_at) }}</dd></div>
                <div><dt>Teacher</dt><dd>{{ nextAssignment.created_by?.display_name || nextAssignment.created_by?.username || 'Teacher' }}</dd></div>
              </dl>
              <button type="button" class="ui-button ui-button--primary" @click="$router.push(`/student/task/${nextAssignment.id}/workspace`)">
                {{ ['submitted', 'reviewed'].includes(nextAssignment.status) ? 'View submission' : 'Continue' }}
              </button>
            </div>
            <div v-else-if="!loading" class="ui-empty">No assignments are available yet.</div>
          </article>

          <article class="workspace-card student-summary-card">
            <SectionHeader title="Your course" description="The class and teacher connected to your workspace." />
            <dl class="student-summary">
              <div><dt>Name</dt><dd>{{ displayName }}</dd></div>
              <div><dt>Class</dt><dd>{{ profile?.class_group || '-' }}</dd></div>
              <div><dt>Teacher</dt><dd>{{ teacherName }}</dd></div>
            </dl>
          </article>
        </div>
      </section>

      <section v-if="activeView === 'assignments'" class="workspace-card assignments-card">
        <SectionHeader title="Assignments" description="Search your writing tasks, check deadlines, and continue your work." />
        <div class="list-toolbar">
          <input v-model="searchTerm" class="ui-input" aria-label="Search assignments" placeholder="Search assignments..." />
          <select v-model="statusFilter" class="ui-select filter-select" aria-label="Filter assignments by status">
            <option value="">All statuses</option>
            <option value="not_started">Not started</option>
            <option value="in_progress">In progress</option>
            <option value="submitted">Submitted</option>
            <option value="reviewed">Reviewed</option>
          </select>
        </div>
        <div v-if="paginatedAssignments.length" class="table-scroll">
          <table class="ui-table assignments-table">
            <thead>
              <tr>
                <th :aria-sort="ariaSort('title')"><button type="button" class="sort-header" :data-sort-state="sortState('title')" @click="toggleAssignmentSort('title')">Assignment</button></th>
                <th :aria-sort="ariaSort('assigned_at')"><button type="button" class="sort-header" :data-sort-state="sortState('assigned_at')" @click="toggleAssignmentSort('assigned_at')">Assigned</button></th>
                <th :aria-sort="ariaSort('start_at')"><button type="button" class="sort-header" :data-sort-state="sortState('start_at')" @click="toggleAssignmentSort('start_at')">Start</button></th>
                <th :aria-sort="ariaSort('due_at')"><button type="button" class="sort-header" :data-sort-state="sortState('due_at')" @click="toggleAssignmentSort('due_at')">Due</button></th>
                <th :aria-sort="ariaSort('teacher')"><button type="button" class="sort-header" :data-sort-state="sortState('teacher')" @click="toggleAssignmentSort('teacher')">Teacher</button></th>
                <th :aria-sort="ariaSort('status')"><button type="button" class="sort-header" :data-sort-state="sortState('status')" @click="toggleAssignmentSort('status')">Status</button></th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="assignment in paginatedAssignments" :key="assignment.id">
                <td data-label="Assignment">
                  <div class="assignment-main">
                    <strong>{{ assignment.title }}</strong>
                    <span class="line-clamp">{{ assignment.prompt }}</span>
                  </div>
                </td>
                <td data-label="Assigned">{{ formatDate(assignment.assigned_at) }}</td>
                <td data-label="Start">{{ formatDateTime(assignment.start_at) }}</td>
                <td class="deadline" data-label="Due">{{ formatDateTime(assignment.due_at) }}</td>
                <td data-label="Teacher">{{ assignment.created_by?.display_name || assignment.created_by?.username || 'Teacher' }}</td>
                <td data-label="Status"><StatusPill :value="assignment.status" /></td>
                <td data-label="Action">
                  <button type="button" class="ui-button ui-button--primary compact-button" @click="$router.push(`/student/task/${assignment.id}/workspace`)">
                    {{ ['submitted', 'reviewed'].includes(assignment.status) ? 'View submission' : 'Continue' }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <TablePagination
          v-if="filteredAssignments.length"
          :total="filteredAssignments.length"
          :page="assignmentPage"
          :page-size="assignmentPageSize"
          @update:page="assignmentPage = $event"
          @update:page-size="assignmentPageSize = $event"
        />
        <div v-if="!filteredAssignments.length && !loading" class="ui-empty">No assignments match the current filters.</div>
      </section>

      <section v-if="activeView === 'profile'" class="profile-stack">
        <AccountEmailSettings />
        <div v-if="profileMessage" class="ui-notice" role="status">{{ profileMessage }}</div>
        <article class="workspace-card form-card">
          <SectionHeader title="Profile details" description="Update the name shown in your writing workspace." />
          <div class="form-grid">
            <FormField label="Name"><input v-model="profileForm.display_name" class="ui-input" /></FormField>
            <FormField label="Gender"><select v-model="profileForm.gender" class="ui-select"><option value="">Not specified</option><option value="female">Female</option><option value="male">Male</option><option value="other">Other</option></select></FormField>
            <FormField label="Class" helper="Class membership is managed by your school."><input :value="profile?.class_group || '-'" class="ui-input" readonly /></FormField>
            <FormField label="Teacher"><input :value="teacherName" class="ui-input" readonly /></FormField>
            <FormField label="Username" helper="Usernames are managed by your school."><input :value="profile?.username || auth.user?.username" class="ui-input" readonly /></FormField>
          </div>

          <div class="password-section">
            <SectionHeader title="Change password" description="Leave both password fields blank to keep your current password." />
            <div class="form-grid">
              <FormField label="Current password"><input v-model="passwordForm.current_password" class="ui-input" type="password" autocomplete="current-password" /></FormField>
              <FormField label="New password"><input v-model="passwordForm.new_password" class="ui-input" type="password" autocomplete="new-password" /></FormField>
            </div>
          </div>

          <div class="form-actions">
            <button type="button" class="ui-button ui-button--primary" :disabled="profileSaving" @click="saveProfile">{{ profileSaving ? 'Saving...' : 'Save profile' }}</button>
          </div>
        </article>

        <article class="workspace-card grade-history">
          <SectionHeader title="Grade history" description="Review AI and teacher feedback across your submitted writing." />
          <p class="score-disclaimer">AI-generated scores and feedback are for reference only. The score given by the module teacher is final.</p>
          <div v-if="studentGrades.length" class="table-scroll">
            <table class="ui-table grades-table">
              <thead><tr><th>Assignment</th><th>Status</th><th>AI grade</th><th>Teacher grade</th><th>Teacher feedback</th></tr></thead>
              <tbody>
                <tr v-for="row in studentGrades" :key="row.record_id">
                  <td data-label="Assignment"><strong>{{ row.assignment.title }}</strong></td>
                  <td data-label="Status"><StatusPill :value="row.status" /></td>
                  <td data-label="AI grade">{{ formatScore(row.ai_grade) }}</td>
                  <td data-label="Teacher grade">{{ formatScore(row.teacher_grade) }}</td>
                  <td data-label="Teacher feedback">{{ row.teacher_feedback || '-' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="ui-empty">No grade history yet.</div>
        </article>
      </section>
    </div>
  </AppShell>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { experimentApi } from '../experiments/api'
import StudySteps from '../experiments/StudySteps.vue'
import '../experiments/study-ui.css'
const experiments = ref([])
import { useRouter } from 'vue-router'
import { apiFetch } from '../api'
import AppShell from '../components/AppShell.vue'
import FormField from '../components/FormField.vue'
import AccountEmailSettings from '../components/AccountEmailSettings.vue'
import MetricStrip from '../components/MetricStrip.vue'
import PageHeader from '../components/PageHeader.vue'
import SectionHeader from '../components/SectionHeader.vue'
import StatusPill from '../components/StatusPill.vue'
import dashboardHelpers from '../dashboard-helpers'
import { useAuthStore } from '../stores/auth'
import TablePagination from '../components/TablePagination.vue'

const assignments = ref([])
const overview = ref({
  metrics: { assigned: 0, in_progress: 0, submitted: 0, reviewed: 0 },
  continue_record: null,
  upcoming_assignments: [],
  recent_feedback: [],
})
const overviewMessage = ref('')
const loading = ref(true)
const taskError = ref('')
const selectedTaskKey = ref('')
const activeView = ref('task')
const mobileViewport = window.matchMedia('(max-width: 767px)')
const navCollapsed = ref(mobileViewport.matches)
const profileSaving = ref(false)
const profile = ref(null)
const studentGrades = ref([])
const profileMessage = ref('')
const profileForm = ref({ display_name: '', gender: '' })
const passwordForm = ref({ current_password: '', new_password: '' })
const searchTerm = ref('')
const statusFilter = ref('')
const assignmentSort = ref({ key: 'assigned_at', direction: 'desc' })
const assignmentPage = ref(1)
const assignmentPageSize = ref(10)
const auth = useAuthStore()
const router = useRouter()
const { requestJson, resolveStudentTask, studentTaskOptions } = dashboardHelpers

const navItems = [
  { view: 'task', label: 'Task' },
  { view: 'profile', label: 'Profile' },
]

const displayName = computed(() => profile.value?.display_name || auth.user?.display_name || auth.user?.username || 'Student')
const teacherName = computed(() => profile.value?.teacher?.display_name || profile.value?.teacher?.username || '-')
const pageTitle = computed(() => ({
  task: 'Task',
  overview: 'Home',
  assignments: 'Assignments',
  profile: 'Profile',
}[activeView.value]))
const pageDescription = computed(() => ({
  task: 'Read your task instructions and continue from your current step.',
  overview: 'See what needs attention and track your writing progress.',
  assignments: 'Find each writing task, check its status, and open your workspace.',
  profile: 'Manage your student identity, password, and grade history.',
}[activeView.value]))
const studentMetrics = computed(() => [
  { label: 'Assigned', value: overview.value.metrics.assigned, detail: 'Current tasks' },
  { label: 'In progress', value: overview.value.metrics.in_progress },
  { label: 'Submitted', value: overview.value.metrics.submitted },
  { label: 'Reviewed', value: overview.value.metrics.reviewed },
])
const taskOptions = computed(() => studentTaskOptions(assignments.value, experiments.value))
const taskEntry = computed(() => resolveStudentTask(assignments.value, overview.value, experiments.value, selectedTaskKey.value))
const nextAssignment = computed(() => taskEntry.value.assignment)
const taskTitle = computed(() => taskEntry.value.participant?.mode === 'preview' ? nextAssignment.value?.title.replace(/^PREVIEW\s*[—–-]\s*/i, '') : nextAssignment.value?.title)
const taskGuidance = computed(() => {
  if (['paused', 'closed'].includes(taskEntry.value.participant?.status)) return 'This study is not accepting changes. You can view your saved progress.'
  return [
    'First, complete the questionnaire and rate one sample. Your saved answers will be kept.',
    'Pre-test complete. Continue writing your proposal and improve each section with AI.',
    'Proposal submitted. Complete the questionnaire, rate your proposal, then rate the same sample again.',
    'All steps complete. You can view your submitted proposal.',
  ][taskEntry.value.step] || 'View your saved study progress.'
})

const filteredAssignments = computed(() => {
  const query = searchTerm.value.trim().toLowerCase()
  const rows = assignments.value.filter(item => {
    const matchesQuery = !query || `${item.title} ${item.prompt}`.toLowerCase().includes(query)
    const matchesStatus = !statusFilter.value || item.status === statusFilter.value
    return matchesQuery && matchesStatus
  })
  return sortRows(rows, assignmentSort.value, {
    title: item => item.title,
    assigned_at: item => dateValue(item.assigned_at),
    start_at: item => dateValue(item.start_at, Number.MAX_SAFE_INTEGER),
    due_at: item => dateValue(item.due_at, Number.MAX_SAFE_INTEGER),
    teacher: item => item.created_by?.display_name || item.created_by?.username || 'Teacher',
    status: item => labelStatus(item.status),
  })
})
const paginatedAssignments = computed(() => paginateRows(filteredAssignments.value, assignmentPage.value, assignmentPageSize.value))

onMounted(async () => {
  mobileViewport.addEventListener('change', syncNavigationForViewport)
  await Promise.all([loadTask(), loadProfile(), loadGrades()])
})

onBeforeUnmount(() => mobileViewport.removeEventListener('change', syncNavigationForViewport))

function syncNavigationForViewport(event) {
  navCollapsed.value = event.matches
}

async function loadOverview() {
  overviewMessage.value = ''
  const result = await requestJson(apiFetch('overview'), 'Could not load overview.')
  if (!result.ok) {
    overviewMessage.value = result.error
    return
  }
  overview.value = result.data
}

async function loadTask() {
  loading.value = true
  taskError.value = ''
  try {
    const [result, study] = await Promise.all([
      requestJson(apiFetch('assignments'), 'Could not load your task. Please retry.'),
      loadAllParticipations(),
      loadOverview(),
    ])
    if (!result.ok) throw new Error(result.error)
    if (!Array.isArray(result.data.assignments) || !Array.isArray(study.participants)) throw new Error('Could not load your task progress. Please retry.')
    assignments.value = result.data.assignments
    experiments.value = study.participants
  } catch (error) {
    taskError.value = error.message || 'Could not load your task progress. Please retry.'
  } finally {
    loading.value = false
  }
}

async function loadAllParticipations() {
  const participants=[];let batch,offset=0
  do {batch=(await experimentApi(`mine?limit=100&offset=${offset}`)).participants;participants.push(...batch);offset+=100}while(batch.length===100)
  return {participants}
}

async function loadProfile() {
  const res = await apiFetch('auth/profile')
  if (!res.ok) return
  const data = await res.json()
  profile.value = data.profile
  profileForm.value = {
    display_name: data.profile.display_name || data.profile.username || '',
    gender: data.profile.gender || '',
  }
}

async function loadGrades() {
  if (!auth.user?.id) return
  const res = await apiFetch(`students/${auth.user.id}/grades`)
  if (!res.ok) return
  const data = await res.json()
  studentGrades.value = data.grades || []
}

function openNav(view) {
  activeView.value = view
  if (view === 'profile') {
    profileMessage.value = ''
    loadProfile()
    loadGrades()
  }
}

async function saveProfile() {
  profileSaving.value = true
  profileMessage.value = ''
  try {
    const profileRes = await apiFetch('auth/profile', {
      method: 'PATCH',
      body: JSON.stringify(profileForm.value),
    })
    const profileData = await profileRes.json()
    if (!profileRes.ok) throw new Error(profileData.error || 'Could not save profile.')
    profile.value = profileData.profile
    auth.user = { ...auth.user, ...profileData.profile }

    if (passwordForm.value.current_password || passwordForm.value.new_password) {
      const passwordRes = await apiFetch('auth/password', {
        method: 'POST',
        body: JSON.stringify(passwordForm.value),
      })
      const passwordData = await passwordRes.json()
      if (!passwordRes.ok) throw new Error(passwordData.error || 'Could not change password.')
      passwordForm.value = { current_password: '', new_password: '' }
      auth.clearLocalSession()
      await router.push('/login')
      return
    }
    profileMessage.value = 'Profile saved.'
  } catch (error) {
    profileMessage.value = error.message || 'Could not save profile.'
  } finally {
    profileSaving.value = false
  }
}

async function signOut() {
  await auth.logout()
  router.replace('/login')
}

function navClass(view) {
  return activeView.value === view ? 'nav-item active' : 'nav-item'
}

function countStatus(status) {
  return assignments.value.filter(assignment => assignment.status === status).length
}

function dateValue(value, fallback = 0) {
  if (!value) return fallback
  const parsed = new Date(value).getTime()
  return Number.isNaN(parsed) ? fallback : parsed
}

function sortRows(rows, sortState, readers) {
  const reader = readers[sortState.key] || readers.assigned_at
  const direction = sortState.direction === 'desc' ? -1 : 1
  return [...rows].sort((a, b) => compareValues(reader(a), reader(b)) * direction)
}

function paginateRows(rows, page, pageSize) {
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize))
  const safePage = Math.min(Math.max(page, 1), totalPages)
  return rows.slice((safePage - 1) * pageSize, safePage * pageSize)
}

function compareValues(a, b) {
  if (typeof a === 'number' || typeof b === 'number') return Number(a || 0) - Number(b || 0)
  return String(a || '').localeCompare(String(b || ''))
}

function toggleAssignmentSort(key) {
  assignmentSort.value = assignmentSort.value.key === key
    ? { key, direction: assignmentSort.value.direction === 'asc' ? 'desc' : 'asc' }
    : { key, direction: 'asc' }
}

function sortState(key) {
  return assignmentSort.value.key === key ? assignmentSort.value.direction : 'none'
}

function ariaSort(key) {
  const state = sortState(key)
  return state === 'asc' ? 'ascending' : state === 'desc' ? 'descending' : 'none'
}

function formatDate(value) {
  if (!value) return '-'
  return new Intl.DateTimeFormat('en', { year: 'numeric', month: 'short', day: 'numeric' }).format(new Date(value))
}

function formatDateTime(value) {
  if (!value) return '-'
  return new Intl.DateTimeFormat('en', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(new Date(value))
}

function labelStatus(status) {
  return {
    not_started: 'Not started',
    in_progress: 'In progress',
    submitted: 'Submitted',
    reviewed: 'Reviewed',
  }[status] || status
}

function formatScore(value) {
  return value == null ? 'Not graded' : `${value} / 4`
}

function statusClass(status) {
  return ['status-pill', `status-${status}`]
}
</script>

<style scoped>
.student-workspace {
  width: min(100%, 92rem);
  margin: 0 auto;
}

.overview-stack,
.profile-stack {
  display: grid;
  gap: var(--space-6);
}

.overview-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.45fr) minmax(18rem, 0.55fr);
  gap: var(--space-6);
}

.workspace-card {
  min-width: 0;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: var(--radius-surface);
  padding: var(--space-6);
  background: var(--surface);
}

.workspace-card > :deep(.section-header) {
  margin-bottom: var(--space-4);
}

.next-action-card {
  border-top: 3px solid var(--accent);
}

.next-assignment {
  display: grid;
  gap: var(--space-4);
}

.next-assignment__heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-4);
}

.assignment-label,
.next-assignment h2,
.next-assignment__prompt,
.assignment-facts,
.student-summary {
  margin: 0;
}

.assignment-label {
  color: var(--accent-strong);
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.next-assignment h2 {
  margin-top: var(--space-1);
  font-family: var(--font-academic);
  font-size: clamp(1.35rem, 3vw, 1.75rem);
  line-height: 1.2;
}

.next-assignment__prompt {
  display: -webkit-box;
  overflow: hidden;
  color: var(--ink-muted);
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}

.assignment-facts,
.student-summary {
  display: grid;
}

.assignment-facts {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  border-top: 1px solid var(--border);
  border-bottom: 1px solid var(--border);
}

.assignment-facts div {
  padding: var(--space-3) 0;
}

.assignment-facts div + div {
  border-left: 1px solid var(--border);
  padding-left: var(--space-4);
}

.assignment-facts dt,
.student-summary dt {
  color: var(--ink-muted);
  font-size: 0.75rem;
  font-weight: 700;
}

.assignment-facts dd,
.student-summary dd {
  margin: var(--space-1) 0 0;
}

.student-summary div {
  border-bottom: 1px solid var(--border);
  padding: var(--space-3) 0;
}

.student-summary div:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}

.student-summary dd {
  font-family: var(--font-academic);
  font-size: 1.125rem;
  font-weight: 600;
}

.list-toolbar {
  display: flex;
  gap: var(--space-3);
  margin-bottom: var(--space-4);
}

.list-toolbar .ui-input {
  flex: 1;
}

.filter-select {
  width: auto;
  min-width: 11rem;
}

.table-scroll {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: auto;
}

.assignments-table {
  min-width: 74rem;
}

.grades-table {
  min-width: 52rem;
}

.ui-table th:first-child,
.ui-table td:first-child {
  padding-left: 0;
}

.ui-table th:last-child,
.ui-table td:last-child {
  padding-right: 0;
  text-align: right;
}

.ui-table tbody tr:last-child td {
  border-bottom: 0;
}

.sort-header {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  min-height: 2rem;
  border: 0;
  padding: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  font-weight: inherit;
  text-align: left;
}

.sort-header:hover,
.sort-header:focus-visible {
  color: var(--accent-strong);
}

.sort-header::after {
  color: var(--ink-muted);
  content: '↕';
  font-size: 0.75rem;
}

.sort-header[data-sort-state='asc']::after {
  color: var(--accent-strong);
  content: '↑';
}

.sort-header[data-sort-state='desc']::after {
  color: var(--accent-strong);
  content: '↓';
}

.assignment-main {
  display: grid;
  min-width: 14rem;
  gap: var(--space-1);
}

.assignment-main strong {
  font-family: var(--font-academic);
  font-size: 1rem;
}

.assignment-main span {
  color: var(--ink-muted);
  font-size: 0.8125rem;
}

.line-clamp {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.deadline {
  color: var(--ink);
  font-weight: 700;
}

.compact-button {
  min-height: 2rem;
  padding: var(--space-1) var(--space-2);
  font-size: 0.8125rem;
  white-space: nowrap;
}

.form-card {
  max-width: 64rem;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-6);
}

.password-section {
  margin-top: var(--space-6);
  border-top: 1px solid var(--border);
  padding-top: var(--space-6);
}

.form-actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  margin-top: var(--space-6);
}

.task-stack {
  display: grid;
  gap: var(--space-6);
}

.task-card {
  max-width: 62rem;
  border-radius: 14px;
  padding: 28px;
}
.study-ui :deep(.page-header) { padding-bottom: 22px; }
.study-ui :deep(.page-header h1) { font-family: var(--font-ui); font-size: 26px; font-weight: 700; }
.study-ui :deep(.page-header__eyebrow) { display: none; }
.study-ui :deep(.page-header__description) { margin-top: 8px; font-size: 14px; }

.task-card__topline {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-4);
  margin-bottom: var(--space-6);
}

.task-card__topline h2 {
  margin: var(--space-1) 0 0;
  font-family: var(--font-ui);
  font-size: 22px;
}

.task-instructions {
  white-space: pre-line;
  color: var(--ink);
  line-height: 1.75;
}

.teacher-note,
.score-disclaimer {
  margin: var(--space-6) 0;
  border-left: 3px solid var(--accent);
  padding: var(--space-3) var(--space-4);
  background: var(--surface-muted);
  color: var(--ink-muted);
  line-height: 1.55;
}

.teacher-note p,
.score-disclaimer {
  margin-bottom: 0;
}

.task-action {
  flex-shrink: 0;
}
.task-next { display: flex; align-items: center; justify-content: space-between; gap: 24px; padding: 24px; border-radius: 10px; background: var(--surface-muted); }
.task-next h3 { font-size: 20px; margin: 6px 0 8px; }
.task-next p:not(.assignment-label) { margin: 0; color: var(--ink-muted); font-size: 14px; line-height: 1.65; max-width: 36rem; }
.task-return-note { margin: 14px 0 0; }
.task-brief { margin-top: 24px; border-top: 1px solid var(--border); }
.task-brief summary { cursor: pointer; padding: 18px 0; font-weight: 650; font-size: 14px; }
.task-brief summary span { margin-left: 12px; font-size: 12px; color: var(--ink-muted); font-weight: 400; }
.task-brief summary:focus-visible { outline: 3px solid var(--accent); outline-offset: 2px; }
.task-facts { display: flex; flex-wrap: wrap; gap: 24px; margin: 18px 0 0; padding-top: 16px; border-top: 1px solid var(--border); font-size: 13px; }
.task-facts dt { color: var(--ink-muted); }
.task-facts dd { margin: 4px 0 0; }
.task-selector { display: grid; gap: var(--space-2); margin-bottom: var(--space-6); color: var(--ink-muted); }
.task-selector select { width: 100%; min-width: 0; }

.study-preview {
  color: var(--ink-muted);
  font-size: 0.75rem;
  border: 1px solid var(--border);
  border-radius: var(--radius-control, 6px);
  padding: var(--space-2);
}

@media (max-width: 960px) {
  .overview-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 767px) {
  .task-card__topline { flex-wrap: wrap; }
  .task-next { flex-direction: column; align-items: stretch; padding: 18px; gap: 18px; }
  .task-brief summary span { display: block; margin: 6px 0 0; }
  .workspace-card {
    padding: var(--space-4);
  }

  .list-toolbar,
  .next-assignment__heading {
    align-items: stretch;
    flex-direction: column;
  }

  .filter-select {
    width: 100%;
  }

  .form-grid,
  .assignment-facts {
    grid-template-columns: minmax(0, 1fr);
  }

  .assignment-facts div + div {
    border-top: 1px solid var(--border);
    border-left: 0;
    padding-left: 0;
  }

  .compact-button,
  .sort-header,
  .ui-input,
  .ui-select {
    min-height: 44px;
  }

  .sort-header {
    min-width: 44px;
  }

  .table-scroll {
    overflow: visible;
  }

  .ui-table,
  .ui-table tbody {
    display: block;
    width: 100%;
    min-width: 0;
    background: transparent;
  }

  .ui-table thead {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }

  .ui-table tbody tr {
    display: grid;
    width: 100%;
    gap: 0;
    margin-bottom: var(--space-3);
    border: 1px solid var(--border);
    border-left: 3px solid var(--accent);
    border-radius: var(--radius-surface);
    padding: var(--space-3);
    background: var(--surface);
  }

  .ui-table tbody tr:last-child {
    margin-bottom: 0;
  }

  .ui-table td,
  .ui-table th:first-child,
  .ui-table td:first-child,
  .ui-table th:last-child,
  .ui-table td:last-child {
    display: grid;
    grid-template-columns: minmax(6.5rem, 0.4fr) minmax(0, 1fr);
    align-items: start;
    gap: var(--space-3);
    border: 0;
    padding: var(--space-2) 0;
    text-align: left;
    white-space: normal;
  }

  .ui-table td::before {
    color: var(--ink-muted);
    content: attr(data-label);
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.03em;
    text-transform: uppercase;
  }

  .assignment-main {
    min-width: 0;
  }
}
</style>
